/**
 * Keeping a selected region on the same pixels when the photo is rotated.
 *
 * The cropper reports `croppedAreaPixels` in the coordinate space of the ROTATED BOUNDING BOX —
 * the canvas `getCroppedImg` builds by drawing the image at its angle. Rotate, and that space
 * changes both size and orientation, so a selection stored in it stops pointing at the pixels it
 * was drawn around: the marker stayed where the mouse had been while the car turned underneath it.
 *
 * A selection is therefore kept in the IMAGE's own coordinates, which rotation does not touch, and
 * projected into the current bounding box whenever it has to be drawn or cropped.
 *
 * At right angles the projection is exact. At any other angle an upright rectangle becomes a
 * tilted one and the result is its bounding box — slightly larger than what was drawn. That is
 * deliberate: growing keeps everything the operator selected, where shrinking to fit would
 * silently cut part of it away.
 */

export interface Size {
  width: number;
  height: number;
}

export interface Area {
  x: number;
  y: number;
  width: number;
  height: number;
}

const toRadians = (degrees: number): number => (degrees * Math.PI) / 180;

/** The canvas `getCroppedImg` draws a rotated image onto. */
export function boundingBoxSize(width: number, height: number, degrees: number): Size {
  const radians = toRadians(degrees);
  const cos = Math.abs(Math.cos(radians));
  const sin = Math.abs(Math.sin(radians));
  return {
    width: cos * width + sin * height,
    height: sin * width + cos * height,
  };
}

/** The four corners of an area. */
const cornersOf = (area: Area): Array<[number, number]> => [
  [area.x, area.y],
  [area.x + area.width, area.y],
  [area.x, area.y + area.height],
  [area.x + area.width, area.y + area.height],
];

const boundsOf = (points: Array<[number, number]>): Area => {
  const xs = points.map(([x]) => x);
  const ys = points.map(([, y]) => y);
  const x = Math.min(...xs);
  const y = Math.min(...ys);
  return { x, y, width: Math.max(...xs) - x, height: Math.max(...ys) - y };
};

/**
 * A region of the rotated canvas, expressed in the image's own coordinates.
 *
 * Undoing the rotation about the image centre: the bounding box is what the image sweeps out when
 * turned, so a point in it sits at `imageCentre + rotate(-angle, point - boxCentre)`.
 */
export function imageAreaFromBoundingBox(area: Area, image: Size, degrees: number): Area {
  const box = boundingBoxSize(image.width, image.height, degrees);
  const radians = toRadians(degrees);
  const cos = Math.cos(-radians);
  const sin = Math.sin(-radians);

  return boundsOf(
    cornersOf(area).map(([x, y]) => {
      const dx = x - box.width / 2;
      const dy = y - box.height / 2;
      return [
        image.width / 2 + dx * cos - dy * sin,
        image.height / 2 + dx * sin + dy * cos,
      ] as [number, number];
    }),
  );
}

/** The same region projected back onto the canvas for a given rotation — the inverse of above. */
export function boundingBoxAreaFromImage(area: Area, image: Size, degrees: number): Area {
  const box = boundingBoxSize(image.width, image.height, degrees);
  const radians = toRadians(degrees);
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);

  return boundsOf(
    cornersOf(area).map(([x, y]) => {
      const dx = x - image.width / 2;
      const dy = y - image.height / 2;
      return [
        box.width / 2 + dx * cos - dy * sin,
        box.height / 2 + dx * sin + dy * cos,
      ] as [number, number];
    }),
  );
}
